import {
  AggregateUnitData,
  getExtendedHealthScore,
  SiteViewHealthScore,
} from 'features/siteView/aggregateSiteData'
import { getYearlyCostsByUnit } from 'features/siteView/utils/getPlanCostsByUnit'
import { isNil } from 'lodash'
import {
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoUnit,
} from 'types'

import { FilterFunc } from './types/unitFilteringTypes'

export type SiteViewTableData = {
  unitID: string
  // Name Display
  nameDisplayData: {
    assetName: string
    assetDescription: string | undefined
    assetIdentifiers: ReadonlyArray<string>
    unitImageURL: string | undefined
    assetModelType: string | undefined
  }
  // Last Inspected
  lastInspectedData: {
    lastInspectedDate: Date | undefined
    assetVisCardData: {
      unit: GeckoUnit
      relevantAssetModel: GeckoAssetModel | undefined
      relevantComponentDataFrame: GeckoComponentDataFrame | undefined
      relevantComponent: GeckoComponent | undefined
      relevantColorThreshold: GeckoColorScaleThreshold | undefined
    }
  }
  // Health
  healthData:
    | {
        minRLFromYear: number | undefined
        percentBelowTMin: number | undefined
        hasInspection: boolean
        score: SiteViewHealthScore
        riskScore: number | undefined
      }
    | undefined
  // Plan Cost
  planCostData: {
    allPlanCostSum: number | undefined
  }
}

/**
 * Converts the broad, aggregate data from the site view into a format that can be easily used in a table.
 */
export function convertSiteViewAggregateDataToTableFormat(
  aggregateData: ReadonlyArray<AggregateUnitData>,
  dataFilters: FilterFunc,
  isHealthDataLoading: boolean,
  selectedYear: number
): SiteViewTableData[] {
  const planCosts = getYearlyCostsByUnit(aggregateData)

  return aggregateData
    .map((rowData): SiteViewTableData | null => {
      if (!dataFilters(rowData)) {
        return null
      }

      const totalPlanCost = Object.values(
        planCosts[rowData.unitSpecs.unitData.id] ?? {}
      ).reduce((totalCost, { planBaseCost }) => {
        return totalCost + (planBaseCost ?? 0)
      }, 0)

      const relevantComponent = rowData.unitSpecs.childComponents.find(
        (c) =>
          c.id === rowData.mostRecentInspectionData.relevantCDF?.tags.component
      )

      return {
        unitID: rowData.unitSpecs.unitData.id,
        nameDisplayData: {
          assetName:
            rowData.unitSpecs.unitData.data.name ??
            rowData.unitSpecs.unitData.id,
          assetDescription: rowData.unitSpecs.unitData.data.description,
          // TODO ARCH-335: Implement dynamic identifiers
          assetIdentifiers: [rowData.unitSpecs.unitData.data.erp_id].filter(
            (i) => !isNil(i)
          ),
          unitImageURL: rowData.unitSpecs.unitData.data.primary_image,
          assetModelType: rowData.unitSpecs.assetModel?.data.type,
        },
        lastInspectedData: {
          lastInspectedDate: isNil(
            rowData.mostRecentInspectionData.inspection?.data.date
          )
            ? undefined
            : new Date(rowData.mostRecentInspectionData.inspection?.data.date),
          assetVisCardData: {
            unit: rowData.unitSpecs.unitData,
            relevantAssetModel: rowData.unitSpecs.assetModel,
            relevantComponentDataFrame:
              rowData.mostRecentInspectionData.relevantCDF,
            relevantComponent: relevantComponent,
            relevantColorThreshold:
              rowData.mostRecentInspectionData.relevantColorThreshold,
          },
        },
        healthData: isHealthDataLoading
          ? undefined
          : {
              minRLFromYear: rowData.health[selectedYear]?.minRLFromYear,
              percentBelowTMin: rowData.health[selectedYear]?.percentBelowTMin,
              hasInspection: !!rowData.mostRecentInspectionData,
              score: getExtendedHealthScore(
                rowData.health[selectedYear]?.minRLFromYear,
                rowData.health[selectedYear]?.riskScore,
                !!rowData.mostRecentInspectionData
              ),
              riskScore: rowData.health[selectedYear]?.riskScore,
            },
        planCostData: {
          allPlanCostSum: !totalPlanCost ? undefined : totalPlanCost,
        },
      }
    })
    .filter((val) => !isNil(val))
}
